import React, { useMemo } from 'react';

import { is3pProductKey, type ProductKeys3P } from '../../../constants/products';
import { type ThirdPartyConfigsBootstrap } from '../../../constants/schemas/3p-config';
import { isProductSmartlink } from '../../connectors';
import { usePostOfficePlacement } from '../use-post-office-placement';

interface ConnectorForPlacement {
	connectorId: string;
	connectorIndex: number;
	connectorMode: 'full-connector' | 'smart-link-connector';
	connectorName: string;
	oAuthUrl: string;
};

// Shape expected by search-page-results-inline placement AppProps, in post-office/integrated-teams/rovo-expansion/placement-v2/src/search-page-results-inline/index.tsx
type SearchPageResultsInlinePlacementPropsPayload = {
	productSource: string;
	orgName: string;
	availableConnectors: ConnectorForPlacement[];
};

// Build config object with only keys in availableProductsOrder, in that order.
export const orderConfigByProductOrder = (
	configs: ThirdPartyConfigsBootstrap,
	availableProductsOrder: string[],
): ThirdPartyConfigsBootstrap => {
	const ordered = {} as ThirdPartyConfigsBootstrap;
	for (const productKey of availableProductsOrder) {
		const config = configs[productKey as ProductKeys3P];
		if (config) {
			ordered[productKey as ProductKeys3P] = config;
		}
	}
	return ordered;
}

// Map bootstrap 3P configs to the placement's ConnectorProps shape
export const mapToAvailableConnectors = (
	configs: ThirdPartyConfigsBootstrap,
): ConnectorForPlacement[] => {
	const entries = Object.entries(configs).filter(
		(
			entry,
		): entry is [string, NonNullable<ThirdPartyConfigsBootstrap[string]>] => {
			const [productKey, config] = entry;
			return (
				is3pProductKey(productKey) &&
				config != null &&
				config.userNeedsOAuth &&
				config.outboundAuthUrl != null
			);
		},
	);
	return entries.map(([productKey, config], index) => {
		const isSmartlink = isProductSmartlink({
			productKey,
			thirdPartyConfigs: configs,
		});
		const connectorName =
			typeof config.displayName === 'string'
				? config.displayName
				: ""
		return {
			connectorId: config.id,
			connectorIndex: index,
			connectorMode: isSmartlink ? 'smart-link-connector' : 'full-connector',
			connectorName,
			oAuthUrl: config.outboundAuthUrl,
		};
	});
};

interface SearchPageResultsInlinePlacementProps {
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProductsOrder: string[]; // Product keys in display order; connectors will be shown in this order
};

export const SearchPageResultsInlinePlacement = ({
	thirdPartyConfigs,
	availableProductsOrder,
}: SearchPageResultsInlinePlacementProps): React.JSX.Element | null => {
	const { PostOfficePlacement, isMounted } = usePostOfficePlacement();

	const orderedConfigs = useMemo(
		() => orderConfigByProductOrder(thirdPartyConfigs, availableProductsOrder),
		[thirdPartyConfigs, availableProductsOrder],
	);

	const availableConnectors = useMemo(
		() => mapToAvailableConnectors(orderedConfigs),
		[orderedConfigs],
	);

	// the props passed into the placement are still not fully finalized
	const placementProps: SearchPageResultsInlinePlacementPropsPayload = {
		productSource: '', // TODO: remove once AppProps is updated
		orgName: '', // TODO: potentially remove if information isn't available
		availableConnectors,
	};

	if (!isMounted || !PostOfficePlacement) {
		return null;
	}

	return (
		<PostOfficePlacement
			placementId="search-page-results-inline"
			placementProps={placementProps}
		/>
	);
};
